// Página PÚBLICA de reserva (Pilar 1, Etapa 2). Sin auth — la consume el lead
// directo por link o embebida por iframe en el sitio del cliente.
// Llama a los endpoints públicos GET /api/book/:slug/slots y POST /api/book/:slug.
// Los slots vienen como instantes UTC; se muestran en la zona del VISITANTE.

import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Clock, CheckCircle2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePublicAgendaTheme, useEmbedAutoHeight, readPublicAgendaThemeParam } from '~/lib/public-agenda-theme';
import styles from './agenda.$slug.module.css';

const PB_URL = import.meta.env.VITE_PB_URL || 'https://api.lifefocus.agency';

export const Route = createFileRoute('/agenda/$tenant/$slug')({
  component: AgendaPage,
});

interface Slot {
  start: string;
  end: string;
}
interface PageInfo {
  name: string;
  description?: string;
  logo_url?: string;
  duration_minutes: number;
  timezone: string;
  host_name: string;
}

const VISITOR_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

// Atribución para conversiones (gclid/utm). La página vive en app.lifefocus.agency;
// el gclid llega por la URL: Google lo agrega solo si el link de reserva es la URL
// final del anuncio, o el sitio del cliente lo reenvía al iframe (snippet de
// "Compartir"). Se manda al backend, que crea el registro de atribución del lead.
const ATTR_KEYS = ['gclid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
function readAttribution(): Record<string, string> {
  const attr: Record<string, string> = {};
  try {
    const p = new URLSearchParams(window.location.search);
    for (const k of ATTR_KEYS) {
      const v = p.get(k);
      if (v) attr[k] = v.slice(0, 300);
    }
    if (document.referrer) attr.referrer = document.referrer.slice(0, 500);
    attr.landing_page = window.location.href.slice(0, 500);
  } catch {
    // sin window (SSR) — devuelve vacío
  }
  return attr;
}

function dayKey(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: VISITOR_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
function dayLabel(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-CL', { timeZone: VISITOR_TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
function timeLabel(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-CL', { timeZone: VISITOR_TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
  } catch {
    return iso.slice(11, 16);
  }
}

/** Logo de marca fuera de la card (estilo login). Se oculta si el src falla. */
function BrandLogo({ src, alt, invert }: { src?: string; alt: string; invert?: boolean }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={`${styles.brandLogo}${invert ? ` ${styles.brandLogoInvert}` : ''}`}
      onError={() => setBroken(true)}
    />
  );
}

function FocusBrand() {
  return (
    <p className={styles.brand}>
      Agendado con{' '}
      <a href="https://lifefocus.agency/sistema-focus" target="_blank" rel="noopener" className={styles.brandLink}>
        Sistema Focus
      </a>
    </p>
  );
}

/** ¿Estamos embebidos en un iframe (landing Ads)? Query ?embed=1 o frame real. */
function useIsEmbed(): boolean {
  return useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('embed');
      if (q === '1' || q === 'true') return true;
      return window.self !== window.top;
    } catch {
      return true; // cross-origin parent → treated as embed
    }
  }, []);
}

function AgendaPage() {
  usePublicAgendaTheme();
  const embed = useIsEmbed();
  useEmbedAutoHeight(embed);
  const TitleTag: 'h1' | 'h2' = embed ? 'h2' : 'h1';
  const darkLogo = useMemo(() => {
    try {
      return document.documentElement.getAttribute('data-theme') === 'dark'
        || readPublicAgendaThemeParam() === 'dark';
    } catch {
      return false;
    }
  }, []);
  const { tenant, slug } = useParams({ from: '/agenda/$tenant/$slug' });
  const apiBase = `${PB_URL}/api/book/${encodeURIComponent(tenant)}/${encodeURIComponent(slug)}`;
  const [page, setPage] = useState<PageInfo | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<Slot | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Preview del gracias sin reservar: /agenda/{tenant}/{slug}?preview=thanks
  // (solo UI; no crea booking). Útil para pulir branding.
  const previewThanks = (() => {
    try {
      return new URLSearchParams(window.location.search).get('preview') === 'thanks';
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${apiBase}/slots`)
      .then(async (r) => {
        const data = (await r.json().catch(() => ({}))) as {
          message?: string;
          page?: PageInfo;
          slots?: Slot[];
        };
        if (!r.ok) {
          throw new Error(
            data && data.message
              ? String(data.message)
              : 'No pudimos cargar la disponibilidad. Verifica el enlace.',
          );
        }
        return data;
      })
      .then((data) => {
        if (!alive) return;
        setPage(data.page);
        setSlots(data.slots || []);
        setLoadError(null);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err instanceof Error && err.message
            ? err.message
            : 'No pudimos cargar la disponibilidad. Verifica el enlace.',
        );
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [apiBase]);

  const days = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots) {
      const k = dayKey(s.start);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(s);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [slots]);

  useEffect(() => {
    if (!selectedDay && days.length > 0) setSelectedDay(days[0][0]);
  }, [days, selectedDay]);

  const currentDayIndex = days.findIndex((d) => d[0] === selectedDay);
  const currentSlots = currentDayIndex >= 0 ? days[currentDayIndex][1] : [];

  async function handleBook() {
    if (!selectedSlot) return;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!form.name.trim() || !emailOk) {
      setSubmitError('Ingresa tu nombre y un email válido — ahí te enviamos la confirmación.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start: selectedSlot.start, name: form.name, email: form.email, phone: form.phone, attr: readAttribution() }),
      });
      const data = (await res.json().catch(() => ({}))) as { message?: string; slots?: Slot[] };
      if (res.status === 409) {
        setSubmitError('Ese horario se acaba de ocupar. Elige otro, por favor.');
        const fresh = await fetch(`${apiBase}/slots`).then((r) => r.json());
        setSlots(fresh.slots || []);
        setSelectedSlot(null);
        setSubmitting(false);
        return;
      }
      if (!res.ok) {
        throw new Error(
          data && data.message ? String(data.message) : 'No se pudo agendar. Intenta de nuevo.',
        );
      }
      setDone(selectedSlot);
    } catch (err) {
      setSubmitError(
        err instanceof Error && err.message ? err.message : 'No se pudo agendar. Intenta de nuevo.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className={`${styles.shell}${embed ? ` ${styles.shellEmbed}` : ''}`}>
        <div className={styles.card}><Loader2 className={styles.spin} size={28} /></div>
      </div>
    );
  }

  if (loadError || !page) {
    return (
      <div className={`${styles.shell}${embed ? ` ${styles.shellEmbed}` : ''}`}>
        <div className={styles.card}><p className={styles.errorText}>{loadError || 'Página no encontrada.'}</p></div>
        {!embed && <FocusBrand />}
      </div>
    );
  }

  if (done || previewThanks) {
    const when = done ?? slots[0] ?? null;
    const whenDay = when ? dayLabel(when.start) : 'martes, 21 de julio';
    const whenTime = when ? timeLabel(when.start) : '09:30';
    return (
      <div className={`${styles.shell} ${styles.shellConfirm}${embed ? ` ${styles.shellEmbed}` : ''}`}>
        {!embed && <BrandLogo src={page.logo_url} alt={page.name} invert={darkLogo} />}
        <div className={`${styles.card} ${styles.cardConfirm}`}>
          <div className={styles.confirmIcon} aria-hidden>
            <CheckCircle2 size={36} strokeWidth={2} />
          </div>
          <TitleTag className={styles.confirmTitle}>¡Reunión agendada!</TitleTag>
          <p className={styles.confirmText}>
            Te esperamos el <strong>{whenDay}</strong> a las{' '}
            <strong>{whenTime}</strong>
            <span className={styles.confirmTz}> ({VISITOR_TZ.replace('_', ' ')})</span>.
          </p>
          <p className={styles.confirmSub}>Te enviamos la confirmación por correo.</p>
          {previewThanks && !done && (
            <p className={styles.previewBadge}>Vista previa</p>
          )}
        </div>
        {!embed && <FocusBrand />}
      </div>
    );
  }

  return (
    <div className={`${styles.shell}${embed ? ` ${styles.shellEmbed}` : ''}`}>
      {/* En embed el logo de la landing ya está arriba; acá solo estorba y
          en dark el logo oscuro pierde contraste. */}
      {!embed && <BrandLogo src={page.logo_url} alt={page.name} invert={darkLogo} />}
      <div className={`${styles.card}${embed ? ` ${styles.cardEmbed}` : ''}`}>
        <header className={styles.header}>
          <TitleTag className={styles.title}>{page.name}</TitleTag>
          <p className={styles.subtitle}>
            {page.host_name ? `Con ${page.host_name} · ` : ''}{page.duration_minutes} min
          </p>
          {page.description ? <p className={styles.description}>{page.description}</p> : null}
        </header>

        {days.length === 0 ? (
          <p className={styles.empty}>No hay horarios disponibles en este momento.</p>
        ) : (
          <>
            <div className={styles.dayNav}>
              <button
                className={styles.navBtn}
                disabled={currentDayIndex <= 0}
                onClick={() => { setSelectedDay(days[currentDayIndex - 1][0]); setSelectedSlot(null); }}
                aria-label="Día anterior"
              >
                <ChevronLeft size={18} />
              </button>
              <span className={styles.dayLabel}>{selectedDay && dayLabel(currentSlots[0]?.start || selectedDay + 'T12:00:00Z')}</span>
              <button
                className={styles.navBtn}
                disabled={currentDayIndex >= days.length - 1}
                onClick={() => { setSelectedDay(days[currentDayIndex + 1][0]); setSelectedSlot(null); }}
                aria-label="Día siguiente"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className={styles.slotGrid}>
              {currentSlots.map((s) => (
                <button
                  key={s.start}
                  className={`${styles.slotBtn} ${selectedSlot?.start === s.start ? styles.slotBtnActive : ''}`}
                  onClick={() => { setSelectedSlot(s); setSubmitError(null); }}
                >
                  <Clock size={12} aria-hidden /> {timeLabel(s.start)}
                </button>
              ))}
            </div>

            {selectedSlot && (
              <div className={styles.bookForm}>
                <p className={styles.bookFormTitle}>
                  Reservar el {dayLabel(selectedSlot.start)} a las {timeLabel(selectedSlot.start)}
                </p>
                <input
                  className={styles.input}
                  placeholder="Tu nombre"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
                <input
                  className={styles.input}
                  type="email"
                  placeholder="Tu email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
                <input
                  className={styles.input}
                  placeholder="Tu teléfono (opcional)"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
                {submitError && <p className={styles.errorText}>{submitError}</p>}
                <button className={styles.confirmBtn} onClick={handleBook} disabled={submitting}>
                  {submitting ? <><Loader2 size={14} className={styles.spin} /> Agendando…</> : 'Confirmar reserva'}
                </button>
              </div>
            )}
          </>
        )}

        <p className={styles.tzNote}>Horarios en tu zona: {VISITOR_TZ.replace('_', ' ')}</p>
      </div>
      {!embed && <FocusBrand />}
    </div>
  );
}
